import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listBrevoLists,
  importBrevoList,
  listImportedRecipients,
  pasteRecipients,
  importInactiveVendors,
} from "@/lib/brevo.functions";

export function MailingSection() {
  const qc = useQueryClient();
  const fetchLists = useServerFn(listBrevoLists);
  const fetchImported = useServerFn(listImportedRecipients);
  const importFn = useServerFn(importBrevoList);
  const pasteFn = useServerFn(pasteRecipients);
  const importInactiveFn = useServerFn(importInactiveVendors);

  const [pasteLabel, setPasteLabel] = useState<string>("leadi");
  const [pasteText, setPasteText] = useState<string>("");
  const [pasteResult, setPasteResult] = useState<string>("");
  const [inactiveResult, setInactiveResult] = useState<string>("");

  const inactiveMut = useMutation({
    mutationFn: async () => importInactiveFn({ data: { label: pasteLabel.trim() || "leadi" } }),
    onSuccess: (r) => {
      setInactiveResult(`Dodanih: ${r.added} novih neaktivnih trgovin, preskočeno ${r.skipped} že obstoječih (najdenih: ${r.total}).`);
      qc.invalidateQueries({ queryKey: ["marketing-recipients-summary"] });
    },
    onError: (e: any) => setInactiveResult(`Napaka: ${e?.message ?? "neznana"}`),
  });

  const [customerLabel, setCustomerLabel] = useState<string>("stranke");
  const [customerText, setCustomerText] = useState<string>("");
  const [customerResult, setCustomerResult] = useState<string>("");

  const pasteMut = useMutation({
    mutationFn: async () => {
      const lbl = pasteLabel.trim() || "leadi";
      if (!pasteText.trim()) throw new Error("Prilepi vsaj en e-naslov.");
      return pasteFn({ data: { label: lbl, source: "paste", text: pasteText } });
    },
    onSuccess: (r) => {
      setPasteResult(`Dodanih: ${r.added} novih, preskočeno ${r.skipped} že obstoječih (najdenih: ${r.total}).`);
      setPasteText("");
      qc.invalidateQueries({ queryKey: ["marketing-recipients-summary"] });
    },
    onError: (e: any) => setPasteResult(`Napaka: ${e?.message ?? "neznana"}`),
  });

  const customerMut = useMutation({
    mutationFn: async () => {
      const lbl = customerLabel.trim() || "stranke";
      if (!customerText.trim()) throw new Error("Prilepi vsaj en e-naslov.");
      return pasteFn({ data: { label: lbl, source: "paste", text: customerText } });
    },
    onSuccess: (r) => {
      setCustomerResult(`Dodanih: ${r.added} novih v "${customerLabel.trim() || "stranke"}", preskočeno ${r.skipped} že obstoječih (najdenih: ${r.total}).`);
      setCustomerText("");
      qc.invalidateQueries({ queryKey: ["marketing-recipients-summary"] });
    },
    onError: (e: any) => setCustomerResult(`Napaka: ${e?.message ?? "neznana"}`),
  });

  const lists = useQuery({
    queryKey: ["brevo-lists"],
    queryFn: () => fetchLists(),
  });

  const imported = useQuery({
    queryKey: ["marketing-recipients-summary"],
    queryFn: () => fetchImported(),
  });

  const [selectedId, setSelectedId] = useState<string>("");
  const [label, setLabel] = useState<string>("");
  const [result, setResult] = useState<string>("");

  const importMut = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error("Izberi seznam.");
      const lbl = label.trim() || lists.data?.lists.find((l) => l.id === Number(selectedId))?.name || "uvoz";
      return importFn({ data: { listId: Number(selectedId), label: lbl } });
    },
    onSuccess: (r) => {
      setResult(`Uvoženih: ${r.added} novih, preskočeno ${r.skipped} že obstoječih (skupaj v seznamu: ${r.total}).`);
      qc.invalidateQueries({ queryKey: ["marketing-recipients-summary"] });
    },
    onError: (e: any) => setResult(`Napaka: ${e?.message ?? "neznana"}`),
  });

  return (
    <div className="space-y-8">
      <div className="border border-border p-6 space-y-4">
        <h2 className="font-serif text-2xl">Uvoz prejemnikov iz Brevo</h2>
        <p className="text-sm text-muted-foreground">
          Izberi Brevo seznam in uvozi njegove e-naslove v lokalno bazo prejemnikov.
          Podvojeni naslovi se samodejno preskočijo.
        </p>

        {lists.isLoading && <div className="text-sm text-muted-foreground">Nalagam sezname…</div>}
        {lists.error && <div className="text-sm text-destructive">Napaka: {(lists.error as Error).message}</div>}

        {lists.data && (
          <div className="grid gap-4 md:grid-cols-[2fr_1fr_auto] items-end">
            <label className="block">
              <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Brevo seznam</span>
              <select
                value={selectedId}
                onChange={(e) => {
                  setSelectedId(e.target.value);
                  const found = lists.data.lists.find((l) => l.id === Number(e.target.value));
                  if (found && !label) setLabel(found.name);
                }}
                className="w-full border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">— izberi seznam —</option>
                {lists.data.lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} ({l.uniqueSubscribers})
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Oznaka v naši bazi</span>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="npr. UT aktualni rokodelci"
                className="w-full border border-border bg-background px-3 py-2 text-sm"
              />
            </label>

            <button
              onClick={() => importMut.mutate()}
              disabled={importMut.isPending || !selectedId}
              className="bg-accent text-accent-foreground px-5 py-2 text-sm hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
            >
              {importMut.isPending ? "Uvažam…" : "Uvozi"}
            </button>
          </div>
        )}

        {result && <div className="text-sm border border-border bg-muted/30 p-3">{result}</div>}
      </div>

      <div className="border border-border p-6 space-y-4">
        <h2 className="font-serif text-2xl">Ročno dodajanje (npr. leadi)</h2>
        <p className="text-sm text-muted-foreground">
          Prilepi e-naslove (ločeni z vejicami, presledki ali v novi vrsti) in jih shrani pod izbrano oznako.
          Podvojeni naslovi se samodejno preskočijo.
        </p>

        <div className="grid gap-4 md:grid-cols-[1fr_auto] items-end">
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Oznaka</span>
            <input
              type="text"
              value={pasteLabel}
              onChange={(e) => setPasteLabel(e.target.value)}
              placeholder="leadi"
              className="w-full border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <button
            onClick={() => pasteMut.mutate()}
            disabled={pasteMut.isPending || !pasteText.trim()}
            className="bg-accent text-accent-foreground px-5 py-2 text-sm hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
          >
            {pasteMut.isPending ? "Shranjujem…" : "Shrani"}
          </button>
        </div>

        <label className="block">
          <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">E-naslovi</span>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            rows={8}
            placeholder={"ana@example.com\nmiha@example.com, peter@example.com"}
            className="w-full border border-border bg-background px-3 py-2 text-sm font-mono"
          />
        </label>

        {pasteResult && <div className="text-sm border border-border bg-muted/30 p-3">{pasteResult}</div>}

        <div className="border-t border-border pt-4 space-y-3">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="font-serif text-lg">Neaktivne trgovine</h3>
              <p className="text-sm text-muted-foreground">
                Dodaj e-naslove vseh trgovin z neaktivnim članstvom v zgornjo oznako.
              </p>
            </div>
            <button
              onClick={() => inactiveMut.mutate()}
              disabled={inactiveMut.isPending}
              className="bg-accent text-accent-foreground px-5 py-2 text-sm hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
            >
              {inactiveMut.isPending ? "Dodajam…" : `Dodaj neaktivne trgovine v "${pasteLabel.trim() || "leadi"}"`}
            </button>
          </div>
          {inactiveResult && <div className="text-sm border border-border bg-muted/30 p-3">{inactiveResult}</div>}
        </div>
      </div>

      <div className="border border-border p-6 space-y-4">
        <h2 className="font-serif text-2xl">Dodajanje strank (ali drugih prejemnikov)</h2>
        <p className="text-sm text-muted-foreground">
          Prilepi e-naslove in izberi oznako (seznam), kamor se shranijo. Podvojeni naslovi se samodejno preskočijo.
        </p>

        <div className="grid gap-4 md:grid-cols-[1fr_1fr_auto] items-end">
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Oznaka / seznam</span>
            <input
              type="text"
              value={customerLabel}
              onChange={(e) => setCustomerLabel(e.target.value)}
              placeholder="stranke"
              list="existing-labels"
              className="w-full border border-border bg-background px-3 py-2 text-sm"
            />
            <datalist id="existing-labels">
              {imported.data?.labels.map((l) => (
                <option key={l.label} value={l.label} />
              ))}
            </datalist>
          </label>

          <button
            onClick={() => customerMut.mutate()}
            disabled={customerMut.isPending || !customerText.trim()}
            className="bg-accent text-accent-foreground px-5 py-2 text-sm hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
          >
            {customerMut.isPending ? "Shranjujem…" : "Shrani"}
          </button>
        </div>

        <label className="block">
          <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">E-naslovi</span>
          <textarea
            value={customerText}
            onChange={(e) => setCustomerText(e.target.value)}
            rows={8}
            placeholder={"ana@example.com\nmiha@example.com, peter@example.com"}
            className="w-full border border-border bg-background px-3 py-2 text-sm font-mono"
          />
        </label>

        {customerResult && <div className="text-sm border border-border bg-muted/30 p-3">{customerResult}</div>}
      </div>

      <div className="border border-border p-6 space-y-3">
        <h3 className="font-serif text-xl">Uvoženi prejemniki</h3>
        {imported.isLoading && <div className="text-sm text-muted-foreground">Nalagam…</div>}
        {imported.data && (
          <>
            <div className="text-sm text-muted-foreground">
              Skupaj v bazi: <span className="text-foreground font-medium">{imported.data.total}</span>
            </div>
            {imported.data.labels.length === 0 ? (
              <div className="text-sm text-muted-foreground">Še ni uvoženih prejemnikov.</div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="py-2 pr-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Oznaka</th>
                    <th className="py-2 pr-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Število</th>
                  </tr>
                </thead>
                <tbody>
                  {imported.data.labels.map((l) => (
                    <tr key={l.label} className="border-b border-border">
                      <td className="py-2 pr-4">{l.label}</td>
                      <td className="py-2 pr-4 text-accent">{l.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}
